import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {NgxPermissionsService} from 'ngx-permissions';
import {AllDetailsDirective} from './all-details.directive';
import {AllDetailsService} from "./all-details.service";
import {DialogService} from "../../../services/dialog.service";
import {FieldsService} from "../../../services/fields.service";
import {
  ALWAYS_ON_TOP_KEYS,
  ATTACHMENT_KEYS,
  BASE_KEYS,
  FORMAT_THOUSANDS_KEYS,
  KEYS_DISPLAY_NAMES_MAP,
  NAME_KEYS
} from "../../constants/fields.constants";
import {Field} from "../../../common/interfaces/data.interfaces";

@Component({
  selector: 'app-all-details',
  templateUrl: './all-details.component.html',
  styleUrls: ['./all-details.component.scss']
})
export class AllDetailsComponent extends AllDetailsDirective implements OnInit, OnChanges {
  @Input() title;
  @Input() animation = 'move-left';

  @Input() entity: any;

  @Input() maxTableColumns = 4;
  @Input() defaultArraysName = 'Value';
  @Input() moreDetailsFields = [];

  @Input() showHeader = false;
  @Input() showColon = false;
  @Input() visibleFields = [];
  @Input() hiddenFields = [];
  @Input() hiddenFieldsExtra = [];
  @Input() showFieldsInGroups = false;

  @Input() viewInnerObjects = true;
  @Input() hideSensitiveFields = true;
  @Input() viewDetailsFields = [];

  @Input() headerClass = 'mb-3';

  @Input() hasLeftFieldsMark = false;
  @Input() leftFieldMarkOffset = '0px';
  @Input() iconsClass = 'text-muted';
  @Input() dividerClass = 'dotted mt-2';

  // [''] | [{key:'', fields}] for objects withs arrays
  @Input() displayedTablesFields: any[] | { key: string, fields: [] }[] ;
  @Input() hideArrays = false;
  @Input() innerArraysClassList = '';


  @Output() onEdit = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<any>();
  @Output() onCustom = new EventEmitter<any>();

  titleField: any;
  additionalClass = '';
  allFields: Field[];

  arrays: any[];
  fields: any[];

  baseFields: any[] = [];
  statusFields: any[] = [];
  nameFields: any[] = [];

  // fields not displayed;
  helperKeys_ = ['typename', '$', 'isFirstCommenter', 'mod', 'reportQuery'];

  isAdmin = false;
  userIsInternal = false;
  userUid;


  constructor(
    @Inject(MAT_DIALOG_DATA) data,
    private dialogRef: MatDialogRef<AllDetailsComponent>,
    private permissionsService: NgxPermissionsService,
    public vs: DialogService,
    public fs: FieldsService,
    public override as: AllDetailsService,
  ) {
    super();
    // can be a modal or just a component
    this.setInputsFromData(data);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadFields();
  }

  ngOnInit() {
    this.loadFields();
  }


  combineVisibleKeys(visibleKeys, tableKeysObjects) {
    if (!tableKeysObjects.length) return visibleKeys;
    let tableKeys = tableKeysObjects?.map(o => typeof o === 'string' ? o : o?.key);
    tableKeys = tableKeys?.filter(s => !this.ds.valueIncludes(s, visibleKeys.map(s => s.split(' ')?.[0]))) ?? [];
    return visibleKeys?.concat(tableKeys);
  }


  async loadFields() {
    this.setAppearance();

    try {
      this.userIsInternal = localStorage?.getItem('userType') === 'INTERNAL';
    } catch {
    }

    // get the fields;
    this.hiddenFields = this.ds.mergeUnique(this.hiddenFields, this.hiddenFieldsExtra);
    this.isAdmin = (await this.permissionsService.hasPermission(['ROLE_SUPPER_ADMIN']));
    this.hiddenFields = this.hiddenFields.concat(this.helperKeys_);
    if (this.visibleFields.length) this.visibleFields = this.combineVisibleKeys(this.visibleFields, this.displayedTablesFields);

    this.allFields = this.getVisibleFields(this.entity, this.visibleFields);

    // categorize fields // [namefields | fields(details)] // arrays // clear empty fields
    this.arrays = this.allFields.filter(f => this.ds.isArray(f.value) && f?.value?.length);
    this.allFields = this.allFields.filter(f => this.fs.isADefinedField(f));
    if (this.showFieldsInGroups) this.setFieldsGroups(this.allFields.filter(f => !this.ds.isArray(f.value)));
    else this.nameFields = this.allFields.filter(f => this.ds.isValue(f.value) && !this.ds.isArray(f.value));

    // set header title
    if (!this.title) this.title = this.nameFields?.[0]?.value ?? this.fs.getFirstNameField(this.entity)?.value;
    if (this.ds.isObject(this.title)) this.title = this.fs.getFirstNameField(this.title)?.value ?? 'ALL DETAILS';

    // balance the table
    if (this.appearance === 'table' && this.nameFields.length % 2 !== 0) {
      this.nameFields = this.nameFields.concat(this.getVisibleFields({' ': ' '}, [], false));
    }
  }

  setFieldsGroups(allFields) {
    this.nameFields = [];
    // this.statusFields = this.fields.filter(x => this.ds.valueIncludes(x.key, this.statusKeys_));
    NAME_KEYS.forEach(s => allFields.forEach(f => {
      if (this.ds.spacelesslyIncludes(f.key, s)) this.nameFields.push(f);
    }));
    this.baseFields = allFields.filter(x => this.ds.valueIncludes(this.ds.trim(x.key), BASE_KEYS));
    this.fields = allFields.filter(x => !this.ds.valueIncludes(x.key, [...NAME_KEYS, ...BASE_KEYS]));
    // keep on grouping fields if fewer// dont group if visible fields
    if (this.getVisibleCount(this.statusFields) < 3) this.nameFields = this.nameFields.concat(this.statusFields);
    if (this.getVisibleCount(this.fields) < 3) this.nameFields = this.nameFields.concat(this.fields);
    if (this.getVisibleCount(this.baseFields) < 3) this.nameFields = this.nameFields.concat(this.baseFields);
  }

  getVisibleFields(object, visibleFields_ = [], hideUndefined = true) {
    //object can also be a string | object | array | number
    const temp = {};
    if (!this.ds.isObject(object)) (temp[this.defaultArraysName] = object) && (object = temp);
    let allFields: Field[] = this.fs.fieldsArrayFromObj(object);

    if (visibleFields_?.length) allFields = this.filterVisibleOnly(allFields, visibleFields_);
    allFields = allFields.map(field => this.mapField(object, field));
    if (hideUndefined) allFields = allFields.filter(f => this.ds.isDefined(f.value) && !!f?.key);

    if (this.moreDetailsFields.find(s => this.ds.valueIncludes(s, 'order'))) allFields = allFields.sort((a, b) => a?.order - b?.order);
    if (!this.visibleFields?.length && ALWAYS_ON_TOP_KEYS.length) allFields = this.orderFieldsByTopKeys(allFields);

    return allFields;
  }

  orderFieldsByTopKeys(fields: Field[]) {
    const topFields = [];
    ALWAYS_ON_TOP_KEYS.reverse()?.forEach((top_, index) => {
      const topField = fields.find(f => this.ds.valueIncludes(f.key, top_));
      topField && (fields = fields.filter(f => f.key !== topField.key)) && topFields.splice(0, 0, topField);
    });
    return topFields.concat(fields);
  }

  filterVisibleOnly(fields: Field[], visibleFields_) {
    fields = fields.filter(field => this.ds.valueIncludes(field.key, visibleFields_.map(s => this.as.getKeyFromFieldString(s)?.split('.')?.[0])));
    fields = visibleFields_.map(s => ({
      ...fields.find(f => this.ds.valueIncludes(f.key, this.as.getKeyFromFieldString(s)?.split('.')?.[0])),
      fieldString: s
    }));
    fields = fields.map((f, index) => ({...f, index}));
    return fields;
  }

  getFieldString(field: Field) {
    const fieldString = this.moreDetailsFields.find(s => field.key === this.as.getKeyFromFieldString(s)?.split('.')[0]);
    return fieldString ?? field.fieldString ?? field.key;
  }

  mapField(object, field) {
    const s = field.fieldString = this.getFieldString(field);
    field.value = this.getObjectFieldValue(object, field);
    field.isArray = this.ds.isArray(field.value);
    field.key = this.as.getKeyFromFieldString(s);
    field.class = this.as.getClassFromFieldString(s);

    field = {
      ...field,
      name: this.getNameFromFieldString(s),
      ////////////////////////////////
      icon: this.as.getIconFromFieldString(s),
      order: this.as.getPropertyFromFieldString(s, 'order'),
      class_: this.getFieldClasses(field),
      iconClassList: this.as.getIconClassFromFieldString(s),
      ////////////////////////////////
      hidden: this.isHiddenField(field),
      sensitive: this.hideSensitiveFields && this.fs.isSensitiveField(field),
      hasTabularDisplay: this.appearance === 'table' || this.getArrayDisplayFields(field)?.length ? true : this.as.checkIfHasTabularDisplay(s),
      innerVisibleFields: this.getFieldInnerVisibleKeys(field),
      //////number,string,boolean,array,object,null
      type: this.ds.getType(field.value),
      //('attachmentField' | 'commentField' | 'commentArray'| 'attachmentArray' | 'percentage')
      semanticType: this.fs.getFieldSemanticType(field)
    };

    return field;
  }

  getFieldClasses(field: Field) {
    const isArr = Array.isArray(field.value);
    return this.as.mergeClasses(field.class, isArr ? this.arrayFieldsClass : this.fieldsClass);
  }


  getFieldInnerVisibleKeys(field: Field) {
    if (!Array.isArray(field?.value) || !this.ds.isObject(field?.value?.[0])) return [];
    let innerFieldKeys = this.as.getInnerVisibleFields(field?.fieldString); // option 1
    if (this.displayedTablesFields?.length) innerFieldKeys = this.getArrayDisplayFields(field); //option 2

    const setDefualt = this.appearance === 'table' && !innerFieldKeys.length;
    if (setDefualt) this.fs.fieldsArrayFromObj(field.value[0])?.filter(f => this.fs.isADefinedField(f.value) && !this.fs.isSensitiveField(f) && !this.isHiddenField(f))
      ?.forEach((field, index) => index < this.maxTableColumns && innerFieldKeys.push(field.key));
    return innerFieldKeys;
  }

  getObjectFieldValue(object, field) {
    const fieldString = field?.fieldString;
    const key = this.as.getKeyFromFieldString(fieldString);
    let value = field.value;
    if (key?.includes('.')) value = this.fs.getObjectValue(object, key) ?? field.value;
    else if (this.ds.valueIncludesBoth(fieldString, ['.', ' '])) value = object?.[this.ds.getStringBetween(fieldString, '.')]?.[key] ?? field.value;
    if (this.ds.isArray(value) && this.fs.isCommentField(field)) value = this.mapCommentsArray(value);
    return this.ds.formatIfDate(this.ds.valueIncludes(key, FORMAT_THOUSANDS_KEYS) ? this.ds.separateThousandsIfNumber(value) : value);
  }

  getArrayDisplayFields(arrayField) {
    if (typeof this.displayedTablesFields[0] === 'string') return this.displayedTablesFields;
    return this.displayedTablesFields.find(a => this.ds.valueIncludes(a.key, arrayField.key))?.fields;
  }


  getNameFromFieldString(fieldString) {
    const hasRename = (s, s_) => s_.includes('*') ? this.ds.valueIncludesBoth(s, s_.split(' ')[0].split('*')) : this.ds.valueIncludes(s, s_.split(' ')[0]);
    if (!this.ds.valueIncludes(fieldString, ' as ')) fieldString = KEYS_DISPLAY_NAMES_MAP.find(s_ => hasRename(fieldString, s_)) ?? fieldString;
    const name = this.as.getNameFromFieldString(fieldString);
    return this.ds.capitalizeFirstLetter(this.ds.camelToSpaced(name)).trim();
  }

  mapCommentsArray(comments: any[]) {
    return comments.map(object => ({
      ...object,
      isFirstCommenter: comments[0]?.createdBy === object?.createdBy,
      isOwnComment: this.userUid === object?.createdBy,
      hasActions: this.userUid === object?.createdBy
    }))?.sort((a, b) => b?.createdAt - a?.createdAt);
  }

  consoleFraud = (x): void => console.log('Winner', x);

  getVisibleCount = (fields: Field[]): number => fields?.filter(f => !(f?.hidden || f?.sensitive))?.length;

  hasFieldWithIcon = (fields: Field[]) => !!fields.find(f => !!f.icon);

  isHiddenField = (f: Field) => this.ds.valueIncludes(f?.key, this.hiddenFields);

  viewAttachmentFromObject = (o) => this.viewAttachment(this.ds.getFirstPresentKeyValue(o, ATTACHMENT_KEYS));

  viewAttachment(path, title = 'Attachment', extension = 'pdf') {
    // let data = {path:path,file:false, format:extension, title:title}
    // this.view.openModal(ViewAttachmentComponent, data,'80%','auto',['scale-up']);
    console.log(path);
  }

  viewDetails(item) {
    if (!this.userIsInternal) return;
    const data = {data: item, showHeader: true, animation: this.animation, showFieldsInGroups: true};
    this.vs.openModal({
      component: AllDetailsComponent,
      data,
      width: '80%'
    });
  }

  setInputsFromData(data) {
    this.entity = data.data;
    if (!this.ds.isObject(data)) this.closeDialog(true);
    this.userUid = localStorage.getItem('userUid');

    if (data?.hasOwnProperty('data')) this.showHeader = true;
    if (data?.hasOwnProperty('hideSensitiveFields')) this.hideSensitiveFields = data.hideSensitiveFields;
    if (data?.hasOwnProperty('title')) this.title = data.title;
    if (data?.hasOwnProperty('showGroupLabels')) this.showGroupLabels = data.showGroupLabels;
    if (data?.hasOwnProperty('showFieldsInGroups')) this.showFieldsInGroups = data.showFieldsInGroups;
    if (data?.hasOwnProperty('animation')) this.animation = data.animation;
    if (data?.hasOwnProperty('tableCellsClass')) this.tableCellsClass = data.tableCellsClass;
    if (data?.hasOwnProperty('viewInnerObjects')) this.viewInnerObjects = data.viewInnerObjects;
    if (data?.hasOwnProperty('viewDetailsFields')) this.viewDetailsFields = data.viewDetailsFields;

    if (data?.visibleFields?.length) this.visibleFields = data.visibleFields;
    if (data?.hiddenFields?.length) this.hiddenFields = data.hiddenFields;
    if (data?.showHeader) this.showHeader = true;
    //if(!this.hideSensitiveFields) this.hideSensitiveFields = this.isAdmin
  }

  closeDialog = (close = true): void => close && this.dialogRef.close();

}
